import React from 'react'
import {Heading, Link, Box} from '@miro-site/core'

import {ISecondaryNavigationGroup} from '../api/interfaces'
import Grid from './Grid'
import SecondaryNavigationItem from './SecondaryNavigationItem'

import styled, {useTheme, DefaultTheme} from 'styled-components'
import HeaderContainer from './HeaderContainer'

type SecondaryNavigationProps = {
  id: string
  index: number
  prevNavigation: number | null
  nextNavigation: number | null
  navigationGroups: ISecondaryNavigationGroup[]
  highlightedContent?: React.ReactElement
}

type Expandable = {
  $index: number
  $isExpanded: boolean
  $prevNavigation: number | null
  $nextNavigation: number | null
}

type ExpandableColumn = React.ComponentProps<typeof Grid.Column> & Expandable

type ExpandableBox = React.ComponentProps<typeof Box> & Expandable

const ViewAllLink = styled(Link).attrs(({theme}) => ({
  $mt: theme.header.secondaryNavigation.viewAllLink.marginTop,
  $variant: theme.header.secondaryNavigation.viewAllLink.variant,
  $color: theme.header.secondaryNavigation.viewAllLink.color,
  withArrow: theme.header.secondaryNavigation.viewAllLink.withArrow,
  $styles: {
    fontSize: theme.header.secondaryNavigation.viewAllLink.fontSize,
    lineHeight: theme.header.secondaryNavigation.viewAllLink.lineHeight,
  },
}))``

const SecondaryNavigationWrapper = styled(Box).attrs<ExpandableBox>(
  ({$isFixed, $isExpanded, theme: {header}}) => {
    const expandedStyles = {
      pointerEvents: 'all',
      visibility: 'visible',
    }

    const collapsedStyles = {
      pointerEvents: 'none',
      visibility: 'hidden',
    }

    return {
      $py: header.secondaryNavigation.verticalPadding,
      $styles: {
        position: 'absolute',
        zIndex: 8,
        left: 0,
        top: header.height,
        width: '100%',
        maxHeight: `calc(100vh - ${header.bottomLinks.height + header.height}px)`,
        overflow: 'auto',
        ...($isExpanded ? expandedStyles : collapsedStyles),
      },
    }
  },
)<ExpandableBox>``

const columnStyles = (
  theme: DefaultTheme,
  isExpanded: boolean,
  index: number,
  nextNavigation: number | null,
) => {
  const translateOffset = theme.header.secondaryNavigation.animation.translateOffset

  const styles = {
    background: theme.header.secondaryNavigation.background,
    transition: theme.header.secondaryNavigation.animation.columns,
  }

  if (isExpanded) {
    return {
      opacity: 1,
      transform: 'translate(0, 0)',
      ...styles,
    }
  }

  if (nextNavigation === null) {
    return {
      opacity: 0,
      transform: `translateY(-${translateOffset}px)`,
      ...styles,
    }
  }

  return {
    opacity: 0,
    transform:
      index > nextNavigation
        ? `translateX(${translateOffset}px)`
        : `translateX(-${translateOffset}px)`,
    ...styles,
  }
}

const AnimatedColumn = styled(Grid.Column)<ExpandableColumn>`
  ${({theme, $index, $isExpanded, $nextNavigation}) =>
    theme.normalizeStyles(columnStyles(theme, $isExpanded, $index, $nextNavigation))}
`
const NavigationColumn = styled(AnimatedColumn)`
  transition-delay: ${props =>
    props.$isExpanded && props.$prevNavigation === null
      ? props.theme.header.secondaryNavigation.animation.navigationDelay
      : '0ms'};
`

const HighlightedColumn = styled(AnimatedColumn)`
  transition-delay: ${props =>
    props.$isExpanded && props.$prevNavigation === null
      ? props.theme.header.secondaryNavigation.animation.highlightedDelay
      : '0ms'};
`

const Headline = styled(Heading).attrs(({theme}) => ({
  as: theme.header.secondaryNavigation.headline.as,
  $my: theme.header.secondaryNavigation.headline.verticalMargin,
  $variant: theme.header.secondaryNavigation.headline.variant,
}))``

const SecondaryNavigation = React.forwardRef<HTMLElement, SecondaryNavigationProps>(
  (props: SecondaryNavigationProps, ref): React.ReactElement => {
    const {
      navigationGroups,
      highlightedContent,
      index,
      prevNavigation,
      nextNavigation,

      ...restProps
    } = props

    const theme = useTheme()

    const navigationGrid = highlightedContent ? [null, null, 4, 8, 12] : [null, null, 6, 12, 18]

    const navigationGroupGrid = navigationGrid.map(c => {
      if (c === null) {
        return null
      }

      return navigationGroups.length > 1 ? c / 2 : c
    })

    const navigationItemsGrid = [null, null, 1, 2, 3]
    const highlightedContentGrid = [null, null, 2, 4, 6]

    const isExpanded = index === nextNavigation

    return (
      <SecondaryNavigationWrapper
        {...restProps}
        aria-hidden={!isExpanded}
        ref={ref}
        $index={index}
        $prevNavigation={prevNavigation}
        $nextNavigation={nextNavigation}
        $isExpanded={isExpanded}
      >
        <HeaderContainer>
          <Grid gutter={theme.header.grid.gutter} columns={theme.header.grid.columns}>
            <NavigationColumn
              $index={index}
              $prevNavigation={prevNavigation}
              $nextNavigation={nextNavigation}
              $isExpanded={isExpanded}
              columns={navigationGrid}
            >
              <Grid gutter={theme.header.grid.gutter} columns={navigationGrid}>
                {navigationGroups.map(n => (
                  <Grid.Column columns={navigationGroupGrid} key={n.title}>
                    <Headline>{n.title}</Headline>
                    <Grid as="ul" gutter={theme.header.grid.gutter} columns={navigationGroupGrid}>
                      {n.items.map(i => (
                        <Grid.Column as="li" key={i.title} columns={navigationItemsGrid}>
                          <SecondaryNavigationItem
                            key={i.title}
                            path={i.path}
                            title={i.title}
                            description={i.description}
                          />
                        </Grid.Column>
                      ))}
                    </Grid>
                    {n.viewAllLink && (
                      <ViewAllLink href={n.viewAllLink.path}>{n.viewAllLink.title}</ViewAllLink>
                    )}
                  </Grid.Column>
                ))}
              </Grid>
            </NavigationColumn>

            {highlightedContent && (
              <HighlightedColumn
                $index={index}
                $prevNavigation={prevNavigation}
                $nextNavigation={nextNavigation}
                $isExpanded={isExpanded}
                columns={highlightedContentGrid}
              >
                {highlightedContent}
              </HighlightedColumn>
            )}
          </Grid>
        </HeaderContainer>
      </SecondaryNavigationWrapper>
    )
  },
)

SecondaryNavigation.displayName = 'SecondaryNavigation'

export default SecondaryNavigation
